import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, wanneer, geleden } from '../i18n.js';
import { St } from './Status.jsx';
import Wachtwoordveld from './Wachtwoordveld.jsx';

/**
 * De koppeling met Cisco Umbrella (docs/besluiten/umbrella-koppeling.md): de API-sleutel van de MSSP-console (getest en
 * versleuteld bewaard), en per klant in Umbrella welke klant in ZERO het is. Alleen een gekoppelde klant wordt opgehaald.
 */
export default function UmbrellaKoppeling({ klanten }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['umbrella-koppeling'], queryFn: api.umbrellaKoppeling, refetchInterval: 15_000 });
  const [wijzig, zetWijzig] = useState(false);
  const [melding, zetMelding] = useState(null);
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const k = q.data.data;
  const doe = async (fn, goed) => {
    zetMelding(null);
    try { await fn(); if (goed) zetMelding({ goed: true, tekst: goed }); } catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); }
    client.invalidateQueries({ queryKey: ['umbrella-koppeling'] });
  };
  const bezet = new Set(k.orgs.filter((o) => o.tenant).map((o) => o.tenant.code));

  return (
    <div className="grid-2">
      <div className="stack">
        <section className="card">
          <header><h2>Cisco Umbrella</h2>{k.linked && <span className="meta">{k.syncError ? <St soort="critical">{t('Fout bij ophalen')}</St> : <St soort="good">{t('Gekoppeld')}</St>}</span>}</header>
          {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
          {k.linked && !wijzig ? (
            <>
              <dl className="facts">
                <dt>{t('Ingesteld')}</dt><dd>{wanneer(k.setAt)}{k.setBy ? ` · ${k.setBy}` : ''}</dd>
                <dt>{t('Laatst opgehaald')}</dt><dd>{k.syncedAt ? geleden(k.syncedAt) : t('nog niet')}</dd>
                <dt>{t('Klanten in Umbrella')}</dt><dd>{getal(k.orgs.length)} · {t('{n} gekoppeld', { n: k.orgs.filter((o) => o.tenant).length })}</dd>
              </dl>
              {k.syncError && <div className="callout warn" style={{ marginTop: 10 }}>{k.syncError}</div>}
              <div className="actions">
                <button type="button" className="btn primary" onClick={() => doe(api.umbrellaNu, t('Ophalen gestart; de uitkomst staat zo hieronder.'))}>{t('Nu ophalen')}</button>
                <button type="button" className="btn" onClick={() => doe(api.umbrellaKlantenVernieuwen, t('De klanten uit de console zijn bijgewerkt.'))}>{t('Klanten vernieuwen')}</button>
                <button type="button" className="btn" onClick={() => zetWijzig(true)}>{t('Sleutel wijzigen')}</button>
                <button type="button" className="btn danger" onClick={() => doe(api.verwijderUmbrellaKoppeling)}>{t('Koppeling verwijderen')}</button>
              </div>
              <p className="noot">{t('ADTS ZERO haalt elke vijf minuten op wat Umbrella in de securitycategorieën zag, met vijf minuten vertraging. Alleen lezen: ADTS ZERO wijzigt niets in Umbrella.')}</p>
            </>
          ) : <Instellen onKlaar={(n) => { zetWijzig(false); zetMelding({ goed: true, tekst: t('Gekoppeld; {n} klanten in de console.', { n }) }); client.invalidateQueries({ queryKey: ['umbrella-koppeling'] }); }}
            onAnnuleer={k.linked ? () => zetWijzig(false) : null} />}
        </section>
      </div>

      <div className="stack">
        <section className="card">
          <header><h2>{t('Klanten in Umbrella')}</h2><span className="meta">{getal(k.orgs.length)}</span></header>
          {k.orgs.length ? (
            <table>
              <thead><tr><th>Umbrella</th><th>{t('Klant in ADTS ZERO')}</th><th>{t('Laatst')}</th></tr></thead>
              <tbody>{k.orgs.map((o) => (
                <tr key={o.orgId}>
                  <td>{o.name}<br /><span className="muted mono" style={{ fontSize: 12 }}>{o.orgId}</span></td>
                  <td><select className="input" value={o.tenant?.code ?? ''} aria-label={t('Klant in ADTS ZERO voor {org}', { org: o.name })}
                    onChange={(e) => doe(() => api.koppelUmbrellaKlant(o.orgId, e.target.value || null))}>
                    <option value="">{t('— niet koppelen —')}</option>
                    {klanten.filter((x) => x.active && (!bezet.has(x.code) || x.code === o.tenant?.code)).map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                  </select></td>
                  <td style={{ fontSize: 13 }}>{!o.tenant ? <span className="muted">—</span>
                    : o.syncError ? <span title={o.syncError}><St soort="critical">{t('fout')}</St> <span className="muted">{o.syncError.slice(0, 80)}</span></span>
                    : o.syncedAt ? <>{geleden(o.syncedAt)}<br /><span className="muted">{t('{n} securitygebeurtenissen', { n: getal(o.events) })}</span></>
                    : <span className="muted">{t('nog niet')}</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p className="empty">{t('Nog geen klanten: stel eerst de koppeling in.')}</p>}
          <p className="noot">{t('Koppel elke klant in Umbrella aan de klant in ADTS ZERO: de alerts komen bij die klant, en de klant ziet ze ook in het klantportaal. Een klant die niet gekoppeld is, haalt ADTS ZERO niet op.')}</p>
        </section>
      </div>
    </div>
  );
}

function Instellen({ onKlaar, onAnnuleer }) {
  const [v, zetV] = useState({ key: '', secret: '' });
  const [fout, zetFout] = useState(null);
  const [bezig, zetBezig] = useState(false);
  return (
    <form className="form" onSubmit={async (e) => {
      e.preventDefault(); zetFout(null); zetBezig(true);
      try { onKlaar((await api.zetUmbrellaKoppeling({ key: v.key.trim(), secret: v.secret.trim() })).data.customers); }
      catch (err) { zetFout(t(err.details ? Object.values(err.details)[0] : err.message)); } finally { zetBezig(false); }
    }}>
      {fout && <div className="melder rood">{fout}</div>}
      <label className="field"><span>API key</span>
        <input className="input mono" value={v.key} onChange={(e) => zetV((x) => ({ ...x, key: e.target.value }))} autoComplete="off" /></label>
      <label className="field"><span>Key secret</span>
        <Wachtwoordveld waarde={v.secret} zet={(w) => zetV((x) => ({ ...x, secret: w }))} autoComplete="new-password" /></label>
      <div className="actions">
        <button className="btn primary" disabled={bezig || !v.key.trim() || !v.secret.trim()}>{bezig ? t('Bezig…') : t('Testen en opslaan')}</button>
        {onAnnuleer && <button type="button" className="btn" onClick={onAnnuleer}>{t('Annuleren')}</button>}
      </div>
      <p className="noot">{t('Maak de sleutel in de MSSP-console onder Console Settings › API Keys, met alleen lezen op: Admin › Customers, Reports › Granular Events, Reports › Aggregations en Deployments › Virtual Appliances. ADTS ZERO test hem eerst en bewaart hem versleuteld; hij komt niet terug in het scherm.')}</p>
    </form>
  );
}
